import i18next from 'i18next';
import {Map, List, Set} from 'immutable';

import createSelector from 'web-app/util/custom-create-selector';
import ChildBehaviorIds from 'web-app/behaviors/child-behavior-ids';
import PlanBehaviorIds from 'web-app/behaviors/plan-behavior-ids';
import * as SessionSelectors from 'web-app/react/entities/sessions/selectors';
import * as ProductSelectors from 'web-app/react/entities/products/selectors';
import PricingGroupBehaviors from 'web-app/behaviors/pricing-group-behavior-ids';
import {PricingType} from 'web-app/react/entities/common/pricing';
import Session from 'web-app/react/entities/sessions/model';
import * as ChildSelectors from 'web-app/react/entities/children/selectors';
import ProductsAndSessionsBehaviors from 'web-app/behaviors/products-and-sessions-behavior-ids';
import * as CostHelpers from 'web-app/react/entities/plans/cost-helpers';
import * as DiscountPresetSelectors from 'web-app/react/entities/discounts/presets/selectors';
import * as Helpers from 'web-app/react/entities/plans/helpers';
import {hasValue} from 'web-app/util/typescript';

export const AUTOMATIC_PRICING_GROUP_ID = 'AUTOMATIC_PRICING_GROUP_ID';

export const getChildId = state => state.plans.managePlan.childId;
export const validOn = state => state.plans.managePlan.validOn;
export const validTo = state => state.plans.managePlan.validTo;
export const child = state => ChildSelectors.getChildByProps(state, {childId: getChildId(state)});
export const planBehaviors = state => state.plans.managePlan.planBehaviors;
export const originalPlanBehaviors = state => state.plans.managePlan.originalPlanBehaviors;
export const billingProfiles = state => state.plans.managePlan.billingProfiles;
export const attendanceSchedules = state => state.plans.managePlan.attendanceSchedules;
export const showEdit = state => state.plans.managePlan.showEdit;
export const institutionIdFromState = state => state.plans.managePlan.institutionId;

export const hideBillingProfileSelect = createSelector(
    billingProfiles,
    billingProfiles =>
        billingProfiles.size === 1 && billingProfiles.first().billingScheme.id === CostHelpers.BILLING_MONTHLY,
);

export const showFunding = createSelector(
    state => state.plans.managePlan.sessionBehaviors,
    sessionBehaviors => sessionBehaviors.includes(ProductsAndSessionsBehaviors.CanExcludeFromPF),
);

export const canEditCustomDiscount = createSelector(child, child =>
    child ? child.behaviors.includes(ChildBehaviorIds.CanSetCustomDiscount) : true,
);

export const editPlan = state => state.plans.managePlan.editPlan;
export const editPlanId = createSelector(editPlan, editPlan => editPlan.id);
export const pricingGroupId = createSelector(editPlan, editPlan => editPlan.pricingGroupId);
const apiPlanParts = createSelector(editPlan, editPlan => editPlan.planParts);

export const planPublicFundingSettings = createSelector(editPlan, editPlan => editPlan.publicFundingSettings);

export const selectedBillingProfileIds = createSelector(apiPlanParts, planParts =>
    planParts
        .map(part => part.billingProfileId)
        .filter(id => id)
        .toArray(),
);

export const sessionBookings = createSelector(apiPlanParts, planParts =>
    planParts.flatMap(part => part.sessionBookings),
);

export const productBookings = createSelector(apiPlanParts, planParts =>
    planParts.flatMap(part => part.productBookings),
);

export const firstPlanPart = createSelector(apiPlanParts, planParts => planParts.first());

// DEPRECATED: For now we can do this. Since the places we are using it we are looking for actuals/monthly
// and we only support multiple plan parts with the same billing scheme
export const getFirstBilling = createSelector(firstPlanPart, firstPlanPart => firstPlanPart.billing);

export const planStates = createSelector(editPlan, editPlan => editPlan.planStates);

export const activePlanState = createSelector(planStates, Helpers.getActivePlanState);

export const activePublicFundingPreviewPlanState = createSelector(
    state => state.plans.managePlan.publicFundingPreviewPlanStates,
    Helpers.getActivePlanState,
);

export const activePublicFunding = createSelector(activePlanState, activePlanState =>
    activePlanState ? activePlanState.publicFunding : null,
);

export const institutionIdPropsFromState = state => ({
    institutionId: institutionIdFromState(state),
});

const statePricingGroups = createSelector(
    state => state.plans.managePlan.pricingGroups,
    pricingGroups => pricingGroups,
);

export const canChooseAutoMove = createSelector(
    state => state.plans.managePlan.pricingGroupBehaviors,
    pricingGroupBehaviors => pricingGroupBehaviors.includes(PricingGroupBehaviors.CanChooseAutoPricingGroup),
);

export const allDiscountPresets = createSelector(
    state => DiscountPresetSelectors.getPresetsByInstitutionId(state, institutionIdPropsFromState(state)),
    presets => presets,
);

export const activePricingGroupId = createSelector(activePlanState, pricingGroupId, (activePlanState, pricingGroupId) =>
    activePlanState ? activePlanState.pricingGroupId : pricingGroupId || null,
);

export const allPricingGroupsOptions = createSelector(
    canChooseAutoMove,
    statePricingGroups,
    (canChooseAutoMove, pricingGroups) => {
        const mappedPricingGroups = pricingGroups.map(pg => ({
            ...pg,
            id: pg.pricingGroupId,
            value: pg.pricingGroupId,
            label: pg.title,
        }));

        if (canChooseAutoMove) {
            return List([
                {
                    id: '',
                    deleted: false,
                    value: AUTOMATIC_PRICING_GROUP_ID,
                    label: i18next.t('plans.managePlan.automatic'),
                },
            ])
                .concat(mappedPricingGroups)
                .toArray();
        }

        return mappedPricingGroups.toArray();
    },
);

export const isValidPricingGroupOption = createSelector(
    canChooseAutoMove,
    pricingGroupId,
    (canChooseAutoMove, pricingGroupId) => Boolean(canChooseAutoMove || pricingGroupId),
);

export const pricingGroupOptions = createSelector(
    allPricingGroupsOptions,
    activePricingGroupId,
    (pricingGroupsOptions, activePricingGroupId) =>
        pricingGroupsOptions.filter(pg => !pg.deleted || pg.pricingGroupId === activePricingGroupId),
);

export const activePricingGroup = createSelector(
    activePlanState,
    allPricingGroupsOptions,
    (activePlanState, pricingGroups) =>
        pricingGroups.find(pg => activePlanState && pg.pricingGroupId === activePlanState.pricingGroupId),
);

export const isAutoMove = createSelector(pricingGroupId, activePricingGroup, (pricingGroupId, activePricingGroup) =>
    Boolean(!pricingGroupId && activePricingGroup),
);

const isAvailableUnderBillingConfiguration = (item, billing) => {
    if (CostHelpers.billingIsActuals(billing)) {
        const firstPricing = item.pricing.first();
        return firstPricing.pricing !== PricingType.MONTHLY_PRICING;
    }
    if (CostHelpers.billingIsMonthly(billing)) {
        const firstPricing = item.pricing.first();
        return firstPricing.pricing === PricingType.MONTHLY_PRICING;
    }
    return true;
};

const pricingGroupIds = createSelector(pricingGroupOptions, pricingGroupOptions =>
    Set(pricingGroupOptions.map(pricingGroup => pricingGroup.pricingGroupId))
        .filter(hasValue)
        .toArray(),
);

const pricingGroupsToFilterBy = createSelector(
    pricingGroupId,
    activePricingGroupId,
    pricingGroupIds,
    (pricingGroupId, activePricingGroupId, pricingGroupIds) => {
        if (pricingGroupId) {
            return [pricingGroupId];
        } else if (activePricingGroupId) {
            return [activePricingGroupId];
        }
        return pricingGroupIds;
    },
);

const sessionOrProductIsValid = (sessionOrProduct, validFrom, pricingGroupIds) => {
    const from = sessionOrProduct.validFrom ? sessionOrProduct.validFrom.isSameOrBefore(validFrom) : true;
    const to = sessionOrProduct.validTo ? sessionOrProduct.validTo.isAfter(validFrom) : true;
    return (
        from &&
        to &&
        pricingGroupIds.every(pricingGroupId =>
            sessionOrProduct.pricing.some(pricing => pricing.pricingGroupId === pricingGroupId),
        )
    );
};

const allSessions = createSelector(
    SessionSelectors.getSessionsState,
    validOn,
    pricingGroupsToFilterBy,
    (sessions, validFrom, pricingGroupIds) =>
        sessions
            .filter(s => {
                return sessionOrProductIsValid(s, validFrom, pricingGroupIds);
            })
            .toList(),
);

export const canChangeDiscountOrFundingFrequency = createSelector(getFirstBilling, firstBilling =>
    CostHelpers.billingSupportsMultipleFrequencies(firstBilling),
);
const statePlanUsedSessions = state => state.plans.managePlan.originalPlanUsedSessions;

const planUsedSessionIds = createSelector(sessionBookings, sessionBookings =>
    sessionBookings
        .map(booking => booking.sessionId)
        .toSet()
        .toList(),
);

export const planUsedSessions = createSelector(
    planUsedSessionIds,
    statePlanUsedSessions,
    SessionSelectors.getSessionsState,
    (sessionIds, stateSessions, allSessions) =>
        sessionIds
            .map(id => {
                const stateSession = stateSessions.find(session => session.sessionId === id);
                if (stateSession) {
                    return stateSession;
                }

                return allSessions.find(session => session.sessionId === id);
            })
            .filter(s => s),
);

export const availableSessions = createSelector(
    allSessions,
    getFirstBilling,
    planUsedSessions,
    showEdit,
    (sessions, firstBilling, planUsedSessions, showEdit) =>
        showEdit
            ? sessions.filter(session => isAvailableUnderBillingConfiguration(session, firstBilling))
            : planUsedSessions,
);

export const availableSessionsMap = createSelector(availableSessions, availableSessions =>
    Map(availableSessions.map(s => [s.sessionId, s])),
);

export const hasInvalidSessionBookings = createSelector(
    sessionBookings,
    availableSessionsMap,
    (sessionBookings, availableSessionsMap) =>
        sessionBookings
            .filter(booking => availableSessionsMap.has(booking.sessionId))
            .some(booking => {
                const session = availableSessionsMap.get(booking.sessionId);
                return !session.flexibleHours && booking.start;
            }),
);

const allProducts = createSelector(
    ProductSelectors.getProductsState,
    validOn,
    pricingGroupsToFilterBy,
    (products, validFrom, pricingGroupIds) =>
        products
            .filter(p => {
                return sessionOrProductIsValid(p, validFrom, pricingGroupIds);
            })
            .toList(),
);

const statePlanUsedProducts = state => state.plans.managePlan.originalPlanUsedProducts;
const planUsedProductIds = createSelector(productBookings, productBookings =>
    productBookings
        .map(booking => booking.productId)
        .toSet()
        .toList(),
);

export const planUsedPackages = createSelector(planUsedSessions, sessions =>
    sessions.filter(session => session.sessionType === Session.SessionType.PACKAGE_SESSION),
);

export const packages = createSelector(availableSessions, sessions =>
    sessions.filter(session => session.sessionType === Session.SessionType.PACKAGE_SESSION),
);

export const planUsedProducts = createSelector(
    planUsedProductIds,
    statePlanUsedProducts,
    ProductSelectors.getProductsState,
    (productIds, stateProducts, availableProducts) =>
        productIds
            .map(id => {
                const stateProduct = stateProducts.find(product => product.productId === id);
                if (stateProduct) {
                    return stateProduct;
                }

                return availableProducts.find(product => product.productId === id);
            })
            .filter(p => p),
);

export const availableProducts = createSelector(
    allProducts,
    getFirstBilling,
    planUsedProducts,
    showEdit,
    (products, firstBilling, planUsedProducts, showEdit) =>
        showEdit
            ? products.filter(product => isAvailableUnderBillingConfiguration(product, firstBilling))
            : planUsedProducts,
);

export const isValidPlanSettings = createSelector(
    canChooseAutoMove,
    editPlan,
    (canChooseAutoMove, editPlan) =>
        (canChooseAutoMove || editPlan.pricingGroupId) && Helpers.isValidPlanSettings(editPlan),
);

const addFundingToPlanParts = (planParts, activePublicFunding, availableSessionsMap) => {
    return planParts.map(planPart => {
        const sessionBookings = planPart.sessionBookings.map(booking => {
            const fundedBooking = activePublicFunding.fundedBookings.find(fb => fb.bookingId === booking.bookingId);
            if (fundedBooking) {
                return booking.merge(fundedBooking);
            }
            return booking;
        });

        if (availableSessionsMap.size > 0) {
            return planPart.merge({
                sessionBookings: sessionBookings.map(booking => {
                    const session = availableSessionsMap.get(booking.sessionId);
                    if (session && session.flexibleHours && !booking.start) {
                        const availability = session.availability.find(av => av.day === booking.day);
                        if (availability) {
                            return booking.merge({
                                start: availability.start,
                                end: availability.end,
                            });
                        }
                    }

                    return booking;
                }),
            });
        } else {
            return planPart.merge({
                sessionBookings,
            });
        }
    });
};

export const planParts = createSelector(
    apiPlanParts,
    showFunding,
    activePublicFunding,
    availableSessionsMap,
    (planParts, showFunding, activePublicFunding, availableSessionsMap) => {
        if (activePublicFunding && showFunding) {
            return addFundingToPlanParts(planParts, activePublicFunding, availableSessionsMap);
        } else {
            return planParts;
        }
    },
);

export const managePlanDataVariables = createSelector(
    institutionIdFromState,
    pricingGroupId,
    validOn,
    selectedBillingProfileIds,
    (institutionId, pricingGroupId, validOn, billingProfileIds) => ({
        institutionId,
        pricingGroupId,
        validOn,
        billingProfileIds,
    }),
);

export const publicFundingMethods = createSelector(planBehaviors, behaviors => {
    if (behaviors.includes(PlanBehaviorIds.ShowPublicFundingOnPlan)) {
        return behaviors.behaviorForId(PlanBehaviorIds.ShowPublicFundingOnPlan).payload.methods;
    }

    return [];
});
